import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import ScrollToTop from "./components/feature/ScrollToTop";
import OfflineBanner from "./components/feature/OfflineBanner";
import { useAuth } from "./hooks/useAuth";
import { useVisitorTracking } from "./hooks/useVisitorTracking";
import { usePresence } from "./hooks/usePresence";
import { useEffect } from "react";
import { supabase } from "./lib/supabase";
import { isLineOaJoined } from "./lib/lineOa";

/** 修復手機鍵盤收起後視窗高度沒復原的問題（iOS Safari / Android WebView）*/
function useMobileKeyboardFix() {
  useEffect(() => {
    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const isInputLike =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable;

      if (!isInputLike) return;

      // 等鍵盤完全收起後再強制捲動，讓瀏覽器重新計算 layout
      setTimeout(() => {
        window.scrollTo({ top: window.scrollY, behavior: 'instant' });
        document.documentElement.style.height = '';
      }, 100);
    };

    // 用 visualViewport API 偵測鍵盤收起（現代瀏覽器支援）
    const vv = window.visualViewport;
    let prevHeight = vv?.height ?? window.innerHeight;

    const handleViewportResize = () => {
      if (!vv) return;
      const currentHeight = vv.height;
      if (currentHeight > prevHeight + 50) {
        setTimeout(() => {
          window.scrollTo({ top: window.scrollY, behavior: 'instant' });
        }, 50);
      }
      prevHeight = currentHeight;
    };

    document.addEventListener('focusout', handleFocusOut, true);
    vv?.addEventListener('resize', handleViewportResize);

    return () => {
      document.removeEventListener('focusout', handleFocusOut, true);
      vv?.removeEventListener('resize', handleViewportResize);
    };
  }, []);
}

function AppInner() {
  const { user } = useAuth();
  useVisitorTracking(user?.id ?? null);
  usePresence(user?.id ?? null);
  useMobileKeyboardFix();

  // 自動補同步：登入後若 localStorage 有 LINE OA 加入記錄，寫入資料庫
  useEffect(() => {
    if (!user?.id) return;
    if (!isLineOaJoined()) return;

    const sync = async () => {
      try {
        const { data } = await supabase
          .from('user_tokens')
          .select('line_oa_joined')
          .eq('user_id', user.id)
          .maybeSingle();

        if (data && !data.line_oa_joined) {
          await supabase
            .from('user_tokens')
            .upsert(
              {
                user_id: user.id,
                line_oa_joined: true,
                line_oa_joined_at: new Date().toISOString(),
              },
              { onConflict: 'user_id', ignoreDuplicates: false }
            );
        }
      } catch {
        // 同步失敗不影響使用
      }
    };

    sync();
  }, [user?.id]);

  return (
    <>
      <OfflineBanner />
      <ScrollToTop />
      <AppRoutes />
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AppInner />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
